<template>
  <div class="navbar-wrapper">
    <nav
      class="navbar navbar-expand-lg navbar-clean fixed-top"
      :style="{ backgroundColor: navBackground }"
    >
      <div class="container">
        <!-- Logo -->
        <a class="navbar-brand" href="/" @click="cerrarMenu">
          <img
            :src="logoPath"
            alt="GM Risk"
            class="navbar-logo"
          />
        </a>

        <!-- Botón hamburguesa -->
        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Abrir menú de navegación"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <!-- Menú -->
        <div
          id="navbarNav"
          ref="navbarNav"
          class="collapse navbar-collapse"
        >
          <ul class="navbar-nav ms-auto mb-0">
            <li
              v-for="(item, index) in navItems"
              :key="index"
              class="nav-item"
            >
              <!-- Enlaces hacia rutas -->
              <a
                v-if="item.route"
                class="nav-link"
                :class="{
                  active:
                    item.text === 'Blog'
                      ? $route.path.startsWith(item.link)
                      : $route.path === item.link
                }"
                href="#"
                @click.prevent="irARuta(item.link)"
              >
                {{ item.text }}
              </a>

              <!-- Enlaces hacia secciones -->
              <a
                v-else
                class="nav-link"
                :class="{
                  active:
                    !isSobreNosotros &&
                    activeSection === item.link &&
                    !(
                      item.link === 'inicio' &&
                      $route.path.startsWith('/blog')
                    )
                }"
                href="#"
                @click.prevent="irASeccion(item.link)"
              >
                {{ item.text }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <!-- Evita que el navbar fixed tape el hero -->
    <div class="navbar-offset" aria-hidden="true"></div>
  </div>
</template>

<script>
export default {
  name: "Navbar",

  data() {
    return {
      logoPath: "/image/logo.png",
      navBackground: "#1e3c63",
      activeSection: "inicio",
      navbarCollapse: null,

      navItems: [
        { text: "Inicio", link: "inicio" },
        { text: "Nosotros", link: "nosotros" },
        { text: "Servicios", link: "servicios" },
        { text: "Seguros de Viaje", link: "viajes" },
        { text: "Socios", link: "socios" },
        {
          text: "Contacto",
          link: "/formulario_contacto",
          route: true
        }
      ]
    };
  },

  computed: {
    isSobreNosotros() {
      return this.$route.path === "/sobre_nosotros";
    },

    isHomePage() {
      return this.$route.path === "/";
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.initNavbar();
      this.handleScroll();
    });

    window.addEventListener("scroll", this.handleScroll, {
      passive: true
    });
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);

    if (this.navbarCollapse) {
      this.navbarCollapse.dispose();
      this.navbarCollapse = null;
    }
  },

  methods: {
    initNavbar() {
      const navbarElement = this.$refs.navbarNav;
      const BootstrapCollapse = window.bootstrap?.Collapse;

      if (!navbarElement || !BootstrapCollapse) {
        console.warn(
          "Bootstrap Collapse no está disponible. Verifica que Bootstrap 5 JS esté cargado."
        );
        return;
      }

      this.navbarCollapse =
        BootstrapCollapse.getOrCreateInstance(navbarElement, {
          toggle: false
        });
    },

    cerrarMenu() {
      if (window.innerWidth < 992 && this.navbarCollapse) {
        this.navbarCollapse.hide();
      }
    },

    irARuta(ruta) {
      this.cerrarMenu();

      if (this.$route.path !== ruta) {
        this.$router.push(ruta);
      }
    },

    irASeccion(seccion) {
      this.cerrarMenu();

      if (this.isHomePage) {
        this.scrollToSection(seccion);
        return;
      }

      this.$router.push("/").then(() => {
        this.$nextTick(() => {
          setTimeout(() => {
            this.scrollToSection(seccion);
          }, 100);
        });
      });
    },

    scrollToSection(seccion) {
      const element = document.getElementById(seccion);

      if (!element) return;

      const navbarHeight = window.innerWidth < 992 ? 72 : 84;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;

      window.scrollTo({
        top: elementPosition - navbarHeight,
        behavior: "smooth"
      });

      this.activeSection = seccion;
    },

    handleScroll() {
      this.navBackground =
        window.scrollY > 50
          ? "rgba(30, 60, 99, 0.96)"
          : "#1e3c63";

      if (this.isSobreNosotros || !this.isHomePage) return;

      const navbarHeight = window.innerWidth < 992 ? 80 : 95;

      const sections = this.navItems
        .filter((item) => !item.route)
        .map((item) => item.link);

      for (const section of sections) {
        const element = document.getElementById(section);

        if (!element) continue;

        const rect = element.getBoundingClientRect();

        if (
          rect.top <= navbarHeight &&
          rect.bottom >= navbarHeight
        ) {
          this.activeSection = section;
          break;
        }
      }
    }
  }
};
</script>

<style scoped>
/* ==================================================
   ESPACIO PARA EL NAVBAR FIJO
================================================== */

.navbar-offset {
  height: 84px;
}

/* ==================================================
   NAVBAR BASE
================================================== */

.navbar-clean {
  width: 100%;
  min-height: 84px;
  padding: 8px 0;
  z-index: 1100;
  transition:
    background-color 0.3s ease,
    box-shadow 0.3s ease;
  box-shadow: 0 5px 20px rgba(9, 27, 51, 0.12);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.navbar-clean .container {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  max-width: 1320px;
  padding-left: 24px;
  padding-right: 24px;
}

/* ==================================================
   LOGO
================================================== */

.navbar-brand {
  flex-shrink: 0;
  padding: 0;
  margin-right: clamp(24px, 4vw, 60px);
}

.navbar-logo {
  display: block;
  width: auto !important;
  height: 64px !important;
  max-width: 180px !important;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.navbar-brand:hover .navbar-logo {
  transform: scale(1.04);
}

/* ==================================================
   MENÚ
================================================== */

.navbar-collapse {
  min-width: 0;
}

.navbar-nav {
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
}

.nav-item {
  flex: 0 0 auto;
}

.navbar-clean .nav-link {
  position: relative;
  display: block;
  color: #ffffff !important;
  font-size: 0.96rem;
  font-weight: 500;
  line-height: 1.2;
  text-decoration: none;
  text-align: center;

  margin: 0 2px;
  padding: 10px 13px !important;
  border-radius: 8px;

  white-space: nowrap !important;
  word-break: normal !important;
  overflow-wrap: normal !important;

  transition:
    background-color 0.25s ease,
    color 0.25s ease,
    transform 0.25s ease;
}

.navbar-clean .nav-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-1px);
}

.navbar-clean .nav-link.active {
  background-color: rgba(255, 255, 255, 0.14);
}

.navbar-clean .nav-link::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 5px;
  width: 0;
  height: 2px;
  border-radius: 10px;
  background-color: #ffffff;
  transform: translateX(-50%);
  transition: width 0.25s ease;
}

.navbar-clean .nav-link:hover::after,
.navbar-clean .nav-link.active::after {
  width: calc(100% - 28px);
}

/* ==================================================
   HAMBURGUESA
================================================== */

.navbar-toggler {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  padding: 7px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  box-shadow: none !important;
}

.navbar-toggler:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.navbar-toggler:focus {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.12) !important;
}

.navbar-toggler-icon {
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%28255,255,255,0.95%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2.2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
}

/* ==================================================
   LAPTOPS PEQUEÑAS
================================================== */

@media (min-width: 992px) and (max-width: 1199.98px) {
  .navbar-clean .container {
    padding-left: 16px;
    padding-right: 16px;
  }

  .navbar-brand {
    margin-right: 18px;
  }

  .navbar-logo {
    height: 52px !important;
    max-width: 150px !important;
  }

  .navbar-clean .nav-link {
    font-size: 0.88rem;
    margin: 0 1px;
    padding: 9px 8px !important;
  }
}

/* ==================================================
   TABLET Y CELULAR
================================================== */

@media (max-width: 991.98px) {
  .navbar-offset {
    height: 72px;
  }

  .navbar-clean {
    min-height: 72px;
    padding: 7px 0;
  }

  .navbar-clean .container {
    flex-wrap: wrap;
    padding-left: 16px;
    padding-right: 16px;
  }

  .navbar-brand {
    margin-right: 0;
  }

  .navbar-logo {
    height: 52px !important;
    max-width: 155px !important;
  }

  .navbar-collapse {
    flex-basis: 100%;
    width: 100%;
    max-height: calc(100vh - 94px);
    overflow-y: auto;

    margin-top: 10px;
    padding: 10px;

    background-color: rgba(20, 49, 82, 0.98);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 14px;
    box-shadow: 0 16px 35px rgba(6, 21, 38, 0.3);
  }

  .navbar-nav {
    width: 100%;
    align-items: stretch;
    gap: 4px;
  }

  .nav-item {
    width: 100%;
  }

  .navbar-clean .nav-link {
    width: 100%;
    margin: 0;
    padding: 12px 14px !important;

    font-size: 0.96rem;
    text-align: left;

    white-space: normal !important;
    word-break: normal !important;
    overflow-wrap: normal !important;
  }

  .navbar-clean .nav-link:hover {
    transform: none;
  }

  .navbar-clean .nav-link::after {
    display: none;
  }
}

/* ==================================================
   CELULARES PEQUEÑOS
================================================== */

@media (max-width: 575.98px) {
  .navbar-offset {
    height: 66px;
  }

  .navbar-clean {
    min-height: 66px;
    padding: 6px 0;
  }

  .navbar-clean .container {
    padding-left: 12px;
    padding-right: 12px;
  }

  .navbar-logo {
    height: 46px !important;
    max-width: 135px !important;
  }

  .navbar-toggler {
    width: 42px;
    height: 42px;
  }

  .navbar-collapse {
    max-height: calc(100vh - 82px);
    margin-top: 8px;
    padding: 8px;
    border-radius: 12px;
  }

  .navbar-clean .nav-link {
    padding: 11px 12px !important;
    font-size: 0.93rem;
  }
}
</style>